import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import tmdbService from '../services/tmdb';
import StorageService from '../utils/storage';
import LazyImage from './LazyImage';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  background: linear-gradient(135deg, #1a1033 0%, #2b1b54 50%, #392667 100%);
  border-radius: 8px;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 400px;
  background-image: url(${props => props.$bgImage});
  background-size: cover;
  background-position: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(26, 16, 51, 0.8) 0%,
      rgba(26, 16, 51, 0.9) 50%,
      #1a1033 100%
    );
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(229, 9, 20, 0.9);
  border: none;
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

  &:hover {
    background: #F40D17;
    transform: scale(1.1);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  }

  svg {
    width: 24px;
    height: 24px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  padding: 40px;
  color: white;
`;

const Header = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 40px;
`;

const Poster = styled.div`
  width: 300px;
  height: 450px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Info = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MainInfo = styled.div`
  margin-bottom: 20px;
`;

const CastSection = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 10px;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  background: ${props => props.$type === 'quality' ? props.$color : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #ffd700;
  font-weight: 500;
`;

const Overview = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin: 20px 0;
  color: rgba(255, 255, 255, 0.9);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
`;

const Button = styled.button`
  background: ${props => props.$primary ? '#E50914' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.$primary ? '#F40D17' : 'rgba(255, 255, 255, 0.2)'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Section = styled.div`
  margin-top: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 20px;
`;

const CastGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const CastCard = styled.div`
  text-align: center;
`;

const CastImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 60px;
  overflow: hidden;
  margin: 0 auto 10px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CastName = styled.div`
  font-weight: 500;
  margin-bottom: 5px;
`;

const CastRole = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
`;

// Componentes de Skeleton para loading
const SkeletonPulse = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const Skeleton = styled.div`
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0.1) 25%, 
    rgba(255, 255, 255, 0.2) 37%, 
    rgba(255, 255, 255, 0.1) 63%
  );
  background-size: 400% 100%;
  animation: ${SkeletonPulse} 1.4s ease infinite;
  border-radius: 4px;
`;

const PosterSkeleton = styled(Skeleton)`
  width: 300px;
  height: 450px;
`;

const TitleSkeleton = styled(Skeleton)`
  width: 60%;
  height: 40px;
  margin-bottom: 20px;
`;

const TextSkeleton = styled(Skeleton)`
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '20px'};
  margin-bottom: 10px;
`;

const CastSkeleton = styled(Skeleton)`
  width: 120px;
  height: 120px;
  border-radius: 60px;
  margin: 0 auto 10px;
`;

const MovieDetailsModal = ({ 
  isOpen, 
  onClose, 
  content,
  onWatch,
  isFavorite,
  onToggleFavorite 
}) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!content) return;

      try {
        setLoading(true);
        setError(null);

        // Verificar cache
        const cacheKey = `tmdb_${content.name}`;
        const cachedData = localStorage.getItem(cacheKey);
        
        if (cachedData) {
          setDetails(JSON.parse(cachedData));
          setLoading(false);
          return;
        }

        const type = content.group?.toLowerCase().includes('series') ? 'tv' : 'movie';
        const metadata = await tmdbService.getMetadata(content.name, type);

        if (metadata) {
          // Salvar no cache
          localStorage.setItem(cacheKey, JSON.stringify(metadata));
          setDetails(metadata);
        } else {
          setError('Dados não encontrados no TMDB');
        }
      } catch (err) {
        console.error('Erro ao buscar detalhes:', err);
        setError('Erro ao carregar informações do TMDB');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && content) {
      fetchDetails();
    }
  }, [isOpen, content]);

  if (!isOpen) return null;

  const getQualityColor = (quality) => {
    switch(quality) {
      case 'SD': return '#666';
      case 'HD': return '#4caf50';
      case 'FHD': return '#2196f3';
      case 'FHD H265': return '#1976d2';
      case 'FHD HDR': return '#0d47a1';
      case '4K': return '#E50914';
      case '4K H265': return '#b71c1c';
      case '4K HDR': return '#7f0000';
      default: return '#666';
    }
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </CloseButton>

        {loading ? (
          <ContentWrapper>
            <Header>
              <PosterSkeleton />
              <Info>
                <TitleSkeleton />
                <TextSkeleton $width="30%" />
                <TextSkeleton $width="40%" />
                <TextSkeleton $width="80%" $height="100px" />
              </Info>
            </Header>
            <Section>
              <SectionTitle>Elenco</SectionTitle>
              <CastGrid>
                {[1, 2, 3, 4].map(i => (
                  <CastCard key={i}>
                    <CastSkeleton />
                    <TextSkeleton $width="80%" />
                    <TextSkeleton $width="60%" />
                  </CastCard>
                ))}
              </CastGrid>
            </Section>
          </ContentWrapper>
        ) : error ? (
          <ContentWrapper>
            <Header>
              <Poster>
                <LazyImage 
                  src={content.logo || '/placeholder.png'} 
                  alt={content.name}
                />
              </Poster>
              <Info>
                <Title>{content.name}</Title>
                <MetaInfo>
                  <Badge 
                    $type="quality" 
                    $color={getQualityColor(content.quality)}
                  >
                    {content.quality}
                  </Badge>
                  <Badge>{content.group}</Badge>
                </MetaInfo>
                <Overview>{error}</Overview>
                <ActionButtons>
                  <Button $primary onClick={onWatch}>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 3L19 12L5 21V3Z" fill="currentColor"/>
                    </svg>
                    Assistir
                  </Button>
                  <Button onClick={onToggleFavorite}>
                    {isFavorite ? (
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="#FFD700"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {isFavorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
                  </Button>
                </ActionButtons>
              </Info>
            </Header>
          </ContentWrapper>
        ) : details ? (
          <>
            <BackgroundImage $bgImage={tmdbService.getImageUrl(details.backdrop_path)} />
            <ContentWrapper>
              <Header>
                <Poster>
                  <LazyImage 
                    src={tmdbService.getImageUrl(details.poster_path) || content.logo || '/placeholder.png'} 
                    alt={details.title || details.name}
                  />
                </Poster>
                <Info>
                  <MainInfo>
                    <Title>{details.title || details.name}</Title>
                    <MetaInfo>
                      <Badge 
                        $type="quality" 
                        $color={getQualityColor(content.quality)}
                      >
                        {content.quality}
                      </Badge>
                      {details.genres?.map(genre => (
                        <Badge key={genre.id}>{genre.name}</Badge>
                      ))}
                      {details.release_date && (
                        <Badge>
                          {new Date(details.release_date).getFullYear()}
                        </Badge>
                      )}
                      {details.runtime && (
                        <Badge>
                          {Math.floor(details.runtime / 60)}h {details.runtime % 60}min
                        </Badge>
                      )}
                      {details.vote_average && (
                        <Rating>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z"/>
                          </svg>
                          {details.vote_average.toFixed(1)}
                        </Rating>
                      )}
                    </MetaInfo>
                    <Overview>{details.overview}</Overview>
                    <ActionButtons>
                      <Button $primary onClick={onWatch}>
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 3L19 12L5 21V3Z" fill="currentColor"/>
                        </svg>
                        Assistir
                      </Button>
                      <Button onClick={onToggleFavorite}>
                        {isFavorite ? (
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="#FFD700"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        {isFavorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
                      </Button>
                    </ActionButtons>
                  </MainInfo>

                  {details.credits?.cast?.length > 0 && (
                    <CastSection>
                      <SectionTitle>Elenco</SectionTitle>
                      <CastGrid>
                        {details.credits.cast.slice(0, 12).map(actor => (
                          <CastCard key={actor.id}>
                            <CastImage>
                              <LazyImage 
                                src={tmdbService.getImageUrl(actor.profile_path, 'w185') || '/placeholder-actor.png'} 
                                alt={actor.name}
                              />
                            </CastImage>
                            <CastName>{actor.name}</CastName>
                            <CastRole>{actor.character}</CastRole>
                          </CastCard>
                        ))}
                      </CastGrid>
                    </CastSection>
                  )}
                </Info>
              </Header>
            </ContentWrapper>
          </>
        ) : null}
      </ModalContent>
    </ModalOverlay>
  );
};

export default MovieDetailsModal; 